/**
 * @fileoverview Connect lead-detail tabs to independently loaded runs, notes, source events, and decision history. `intakeApi.js` owns each read contract; `IntakeAgentRunModals.js` owns run inspection and feedback. These records retain their original provenance. Lazy presentation and selected pagination do not merge them into one complete history or rerun recorded work.
 */

import { useCallback, useContext, useEffect, useState } from 'react';
import AppIcon from '../shared/AppIcon';
import ToastContext from '../../ToastContext';
import {
  fetchIntakeDecisions,
  fetchIntakeLeadEvents,
  fetchIntakeLeadRuns,
  fetchIntakePipedriveNotes,
  fetchIntakeSourceEvents,
} from './intakeApi';
import { EmptyState, IntakeBadge, RecordList } from './IntakeDetailPrimitives';
import { formatDate, getRecordStatusClass, getTimestamp, titleize } from './intakeFormatters';
import { formatJsonBlock } from './IntakeAgentOutput';
import { AgentFeedbackModal, AgentRunDetailModal, useAgentFeedbackModal, useAgentRunDetailModal } from './IntakeAgentRunModals';
import { buildActivityItems } from './intakeActivity';

// Read-only record tabs on the lead detail page: automation run logs, imported
// Pipedrive notes, raw source events, and the combined history feed.



export function RunLogsTab({ leadId, active }) {
  const showToast = useContext(ToastContext);
  const [payload, setPayload] = useState({ pipeline_runs: [], stage_runs: [], agent_runs: [] });
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(

  () => {
    if (!active || loaded || !leadId) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchIntakeLeadRuns(leadId, { page: 1, page_size: 50, type: 'all' })
    .then((response) => {
      if (!cancelled) {
        setPayload(response.data || { pipeline_runs: [], stage_runs: [], agent_runs: [] });
        setLoading(false);
        setLoaded(true);
      }
    })
    .catch((error) => {
      console.error('Failed to load intake run logs:', error);
      if (!cancelled) showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load run logs' });
    })
    .finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [active, leadId, loaded, showToast]);

  const pipelineRuns = payload.pipeline_runs || [];
  const stageRuns = payload.stage_runs || [];
  const agentRuns = payload.agent_runs || [];
  const items = buildActivityItems(pipelineRuns, stageRuns, agentRuns);
  const {
    detail,
    loadingRunId: detailLoadingRunId,
    openAgentRunDetail,
    closeAgentRunDetail
  } = useAgentRunDetailModal();
  const {
    target: agentFeedbackTarget,
    feedbackItems,
    loading: feedbackLoading,
    saving: feedbackSaving,
    loadingRunId: feedbackLoadingRunId,
    openAgentFeedback,
    closeAgentFeedback,
    submitAgentFeedback,
    deactivateAgentFeedback
  } = useAgentFeedbackModal();

  return (
    <div className="intake-tab-panel">
            <RecordList title="Automation Run Logs" loading={loading} empty="No automation run logs yet.">
                {items.map((item) =>
        <div className="intake-record-row" key={item.id}>
                        <div>
                            <strong>{item.label}</strong>
                            <span>{item.detail}</span>
                            <small>{item.time ? formatDate(item.time) : 'No timestamp'}</small>
                        </div>
                        <div className="intake-record-row__actions">
                            <IntakeBadge value={item.statusLabel || item.status} className={getRecordStatusClass(item.status)} />
                            {item.kind === 'agent' &&
              <button type="button" className="intake-button" onClick={() => openAgentRunDetail(item.leadId, item.runId)} disabled={detailLoadingRunId === item.runId}>
                                Details
                            </button>}
                            {item.kind === 'agent' &&
              <button
                type="button"
                className="intake-button intake-button--quiet"
                onClick={() => openAgentFeedback({
                  id: item.runId,
                  lead_id: item.leadId,
                  agent_id: item.agent_id,
                  agent_display_name: item.agent_display_name,
                  agent_key: item.agent_key,
                  status: item.status
                })}
                disabled={feedbackLoadingRunId === item.runId}>
                                <AppIcon icon={feedbackLoadingRunId === item.runId ? 'pi pi-spin pi-spinner' : 'pi pi-comments'} aria-hidden="true" />
                                <span>Feedback</span>
                            </button>}
                        </div>
                    </div>
        )}
            </RecordList>
            <AgentRunDetailModal
              detail={detail}
              onClose={closeAgentRunDetail}
              mode="full" />
            <AgentFeedbackModal
              target={agentFeedbackTarget}
              feedbackItems={feedbackItems}
              loading={feedbackLoading}
              saving={feedbackSaving}
              onSubmit={submitAgentFeedback}
              onDeactivate={deactivateAgentFeedback}
              onClose={closeAgentFeedback} />
        </div>);

}



export function PipedriveNotesTab({ leadId, active }) {
  const showToast = useContext(ToastContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadRows = useCallback(

  async () => {
    setLoading(true);
    try {
      const response = await fetchIntakePipedriveNotes(leadId, { page: 1, page_size: 50 });
      setRows(response.data.pipedrive_notes || []);
      setLoaded(true);
    } catch (error) {
      console.error('Failed to load intake Pipedrive notes:', error);
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load Pipedrive notes' });
    } finally {
      setLoading(false);
    }
  }, [leadId, showToast]);

  useEffect(() => {
    if (active && !loaded) loadRows();
  }, [active, loaded, loadRows]);

  return (
    <div className="intake-tab-panel">
            <div className="intake-section-heading">
                <div>
                    <h2>Pipedrive Notes</h2>
                    <span>Notes imported from the linked Pipedrive lead, person, and organization.</span>
                </div>
            </div>
            <RecordList title="Pipedrive Notes" loading={loading} empty="No Pipedrive notes imported yet.">
                {rows.map((row) =>
        <div className="intake-record-row" key={row.id || row.pipedrive_note_id}>
                        <div>
                            <strong>{titleize(row.source_object_type || 'Pipedrive')} Note</strong>
                            <span>{row.content_text || 'Untitled note'}</span>
                            <small>{titleize(row.source_object_type || 'Pipedrive')} {row.source_object_id || row.pipedrive_note_id} / {formatDate(row.pipedrive_update_time || row.pipedrive_add_time || row.created_at)}</small>
                        </div>
                    </div>
        )}
            </RecordList>
        </div>);

}



export function SourceEventsTab({ leadId, active }) {
  const showToast = useContext(ToastContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [includeRaw, setIncludeRaw] = useState(false);

  const loadRows = useCallback(

  async () => {
    setLoading(true);
    try {
      const response = await fetchIntakeSourceEvents(leadId, { page: 1, page_size: 50, include_raw: includeRaw });
      setRows(response.data.source_events || []);
      setLoaded(true);
    } catch (error) {
      console.error('Failed to load intake source events:', error);
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load source events' });
    } finally {
      setLoading(false);
    }
  }, [includeRaw, leadId, showToast]);

  useEffect(() => {
    if (active && !loaded) loadRows();
  }, [active, loaded, loadRows]);

  useEffect(() => {
    if (active && loaded) loadRows();
  }, [active, includeRaw]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="intake-tab-panel">
            <div className="intake-section-heading">
                <div>
                    <h2>Source Events</h2>
                    <span>Raw ingestion records captured before normalization.</span>
                </div>
                <label className="intake-checkbox-row intake-checkbox-row--boxed">
                    <input type="checkbox" checked={includeRaw} onChange={(event) => setIncludeRaw(event.target.checked)} />
                    <span>Show Raw Payloads</span>
                </label>
            </div>
            <RecordList title="Ingestion Events" loading={loading} empty="No source events recorded.">
                {rows.map((row) =>
        <div className="intake-record-row" key={row.id}>
                        <div>
                            <strong>{titleize(row.source_type)}</strong>
                            <span>{row.external_id || row.idempotency_key}</span>
                            <small>{row.error || `Received ${formatDate(row.received_at)}`}</small>
                            {row.raw_payload_json &&
            <pre className="intake-draft-placeholder" style={{ whiteSpace: 'pre-wrap', textAlign: 'left', marginTop: '0.75rem' }}>
                                {formatJsonBlock(row.raw_payload_json)}
                            </pre>}
                        </div>
                        <div className="intake-record-row__actions">
                            <IntakeBadge value={row.ingestion_status} className={getRecordStatusClass(row.ingestion_status)} />
                        </div>
                    </div>
        )}
            </RecordList>
        </div>);

}



function getDecisionHistoryMessage(row) {
  const details = [];
  if (row.reason) details.push(`Reason: ${row.reason}`);
  if (row.notes) details.push(`Notes: ${row.notes}`);
  if (row.converted_matter_number) details.push(`Matter ${row.converted_matter_number}`);
  return details.join(' / ') || 'Decision recorded';
}



function buildHistoryItems(events, decisions) {
  const eventItems = events
  .filter((event) => !(decisions.length && event.event_type === 'decision_recorded'))
  .map((event) => ({
    id: `event-${event.id}`,
    time: event.created_at,
    fallbackOrder: event.id || 0,
    label: titleize(event.event_type),
    message: event.message,
    meta: `${event.actor_type || 'system'}${event.stage_key ? ` / ${titleize(event.stage_key)}` : ''}`
  }));

  const decisionItems = decisions.map((row) => ({
    id: `decision-${row.id}`,
    time: row.decided_at || row.created_at,
    fallbackOrder: row.id || 0,
    label: `${titleize(row.decision)} Decision`,
    message: getDecisionHistoryMessage(row),
    meta: row.decided_by_actor_email || row.decided_by_actor_id || 'staff'
  }));

  return eventItems.concat(decisionItems).sort((left, right) => {
    const timeDiff = getTimestamp(right.time) - getTimestamp(left.time);
    return timeDiff || right.fallbackOrder - left.fallbackOrder;
  });
}



export function HistoryTab({ leadId, active }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

	  useEffect(() => {
	    if (!active || loaded) return;
	    setLoading(true);
	    Promise.all([
	    fetchIntakeLeadEvents(leadId, { page: 1, page_size: 50 }),
	    fetchIntakeDecisions(leadId)]
	    )
	    .then(

	    ([eventsResponse, decisionsResponse]) => {
	      const historyItems = buildHistoryItems(eventsResponse.data.events || [], decisionsResponse.data.decisions || []);
	      setItems(historyItems);
	      setTotal(historyItems.length);
	      setLoaded(true);
	    })
	    .catch((error) => {
      console.error('Failed to load intake history:', error);
    })
    .finally(() => setLoading(false));
  }, [active, leadId, loaded]);

	  if (loading) {
	    return <EmptyState title="Loading history..." />;
	  }

	  if (!items.length) {
	    return <EmptyState title="No history events yet." />;
	  }

	  return (
	    <div className="intake-tab-panel">
	            <div className="intake-history-count">{total} records</div>
	            <ol className="intake-history">
	                {items.map((item) =>
	        <li key={item.id}>
	                        <div className="intake-history__time">{formatDate(item.time)}</div>
	                        <div className="intake-history__body">
	                            <strong>{item.label}</strong>
	                            <span>{item.message}</span>
	                            <small>{item.meta}</small>
	                        </div>
	                    </li>
        )}
            </ol>
        </div>);

}
